import React, { useEffect, useRef, useState } from "react";
import { MapPin } from "lucide-react";
import { suggestCities } from "../constants/zambianCities";

/**
 * Accessible autocomplete input for Zambian cities.
 * - Suggestions appear as the user types (prefix > substring matches).
 * - Arrow keys navigate, Enter selects, Escape closes.
 * - onSelect(cityName) fires when a suggestion is picked; onChange(value)
 *   fires on every keystroke so the parent can still submit free text.
 */
export default function CityAutocomplete({
  value,
  onChange,
  onSelect,
  onSubmit,
  placeholder = "Lusaka, Livingstone, Ndola…",
  className = "",
  inputTestId = "city-autocomplete-input",
  autoFocus = false,
  leadingIcon = true,
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [suggestions, setSuggestions] = useState([]);
  const boxRef = useRef(null);

  useEffect(() => {
    setSuggestions(suggestCities(value));
    setActive(-1);
  }, [value]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const pick = (city) => {
    onChange(city.name);
    if (onSelect) onSelect(city.name);
    setOpen(false);
  };

  const handleKey = (e) => {
    if (!open || suggestions.length === 0) {
      if (e.key === "Enter" && onSubmit) onSubmit(e);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      if (active >= 0) {
        e.preventDefault();
        pick(suggestions[active]);
      } else if (onSubmit) {
        onSubmit(e);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={boxRef} className={`relative ${className}`}>
      <div className="flex items-center gap-2">
        {leadingIcon && <MapPin className="h-4 w-4 text-slate-400" />}
        <input
          data-testid={inputTestId}
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKey}
          placeholder={placeholder}
          autoFocus={autoFocus}
          autoComplete="off"
          className="w-full bg-transparent py-2 text-sm outline-none"
        />
      </div>

      {open && suggestions.length > 0 && (
        <ul
          data-testid="city-autocomplete-list"
          role="listbox"
          className="absolute left-0 right-0 top-full z-40 mt-2 max-h-72 overflow-auto rounded-2xl border border-slate-200 bg-white py-1 shadow-lg"
        >
          {suggestions.map((c, i) => (
            <li
              key={c.name}
              role="option"
              aria-selected={i === active}
              data-testid={`city-suggestion-${c.name}`}
              onMouseDown={(e) => {
                e.preventDefault();
                pick(c);
              }}
              onMouseEnter={() => setActive(i)}
              className={`flex cursor-pointer items-center justify-between gap-2 px-4 py-2 text-sm ${
                i === active ? "bg-copper/10 text-copper" : "text-slate-700 hover:bg-slate-50"
              }`}
            >
              <span className="flex items-center gap-2">
                <MapPin className="h-3.5 w-3.5" /> {c.name}
              </span>
              <span className="text-xs text-slate-400">{c.province}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
